import { create } from 'zustand';
import { FamilyMember, PersonalInfo, AddRequest, PersonalInfoRequest } from '@/types';
import { api } from '@/lib/api/client';
import { AppError } from '@/utils/errors/AppError';

interface FamilyState {
  members: FamilyMember[];
  personalInfos: PersonalInfo[];
  requests: AddRequest[];
  personalInfoRequests: PersonalInfoRequest[];
  isLoading: boolean;
  error: string | null;
  
  fetchMembers: () => Promise<void>;
  fetchPersonalInfos: () => Promise<void>;
  fetchRequests: () => Promise<void>;
  
  addMember: (member: Omit<FamilyMember, 'id' | 'children'>) => Promise<FamilyMember>;
  addRootMember: (name: string) => Promise<FamilyMember>;
  updateMember: (id: string, updates: Partial<FamilyMember>) => Promise<void>;
  deleteMember: (id: string) => Promise<void>;

  addPersonalInfo: (info: Omit<PersonalInfo, 'id'>) => void;
  updatePersonalInfo: (id: string, updates: Partial<PersonalInfo>) => void;
  deletePersonalInfo: (id: string) => void;

  addRequest: (request: AddRequest) => void;
  updateRequestStatus: (id: string, status: AddRequest['status']) => void;
  updatePersonalInfoRequestStatus: (id: string, status: PersonalInfoRequest['status']) => void;
}

// Helper function to transform snake_case to camelCase
function transformMember(data: any): FamilyMember {
  return {
    id: data.id,
    name: data.name,
    parentId: data.parent_id,
    children: [], // Will be populated after all members are loaded
    isRoot: data.is_root === 1,
    createdBy: data.created_by,
    createdAt: data.created_at,
    updatedAt: data.updated_at,
    deletedAt: data.deleted_at
  };
}

// Helper function to transform personal info
function transformPersonalInfo(data: any): PersonalInfo {
  return {
    id: data.id,
    fullName: data.full_name,
    description: data.description,
    phoneNumber: data.phoneNumber,
    photoUrl: data.photo_url,
    resumeUrl: data.resume_url,
    isResumePublic: data.is_resume_public === 1,
    createdAt: new Date(data.created_at).getTime(),
    updatedAt: new Date(data.updated_at).getTime()
  };
}

// Helper function to build the family tree
function buildFamilyTree(members: FamilyMember[]): FamilyMember[] {
  // Create a map for quick lookups
  const memberMap = new Map<string, FamilyMember>();
  members.forEach(member => memberMap.set(member.id, { ...member }));

  // Build parent-child relationships
  members.forEach(member => {
    if (member.parentId) {
      const parent = memberMap.get(member.parentId);
      if (parent) {
        parent.children = parent.children || [];
        parent.children.push(member.id);
      }
    }
  });

  return Array.from(memberMap.values());
}

export const useFamilyStore = create<FamilyState>((set, get) => ({
  members: [],
  personalInfos: [],
  requests: [],
  personalInfoRequests: [],
  isLoading: false,
  error: null,

  fetchMembers: async () => {
    set({ isLoading: true, error: null });
    try {
      const response = await api.get<any[]>('api/members');
      
      // Transform the data
      const transformedMembers = response.map(transformMember);
      
      // Build the family tree
      const treeMembers = buildFamilyTree(transformedMembers);
      
      set({ members: treeMembers, error: null });
    } catch (error) {
      console.error('Failed to fetch members:', error);
      set({ 
        error: error instanceof AppError ? 
          error.message : 
          'حدث خطأ أثناء تحميل أفراد العائلة'
      });
    } finally {
      set({ isLoading: false });
    }
  },

  fetchPersonalInfos: async () => {
    set({ isLoading: true, error: null });
    try {
      const response = await api.get<any[]>('api/personal-info');
      const transformedInfos = response.map(transformPersonalInfo);
      console.log({transformedInfos});
      set({ personalInfos: transformedInfos, error: null });
    } catch (error) {
      console.error('Failed to fetch personal info:', error);
      set({ 
        error: error instanceof AppError ? 
          error.message : 
          'حدث خطأ أثناء تحميل الأرشيف'
      });
    } finally {
      set({ isLoading: false });
    }
  },

  fetchRequests: async () => {
    set({ isLoading: true, error: null });
    try {
      const response = await api.get<{
        addRequests: any[];
        infoRequests: any[];
      }>('api/requests');

      set({ 
        requests: response.addRequests,
        personalInfoRequests: response.infoRequests,
        error: null 
      });
    } catch (error) {
      console.error('Failed to fetch requests:', error);
      set({ 
        error: error instanceof AppError ? 
          error.message : 
          'حدث خطأ أثناء تحميل الطلبات'
      });
    } finally {
      set({ isLoading: false });
    }
  },

  addMember: async (member) => {
    set({ isLoading: true, error: null });
    try {
      const response = await api.post<any>('api/members', member);
      const newMember = transformMember(response);
      
      // Update local state including parent's children array
      set(state => {
        const updatedMembers = state.members.map(m => {
          if (m.id === member.parentId) {
            return {
              ...m,
              children: [...m.children, newMember.id]
            };
          }
          return m;
        });

        return { 
          members: [...updatedMembers, newMember],
          error: null
        };
      });

      return newMember;
    } catch (error) {
      console.error('Failed to add member:', error);
      set({ 
        error: error instanceof AppError ? 
          error.message : 
          'حدث خطأ أثناء إضافة الفرد'
      });
      throw error;
    } finally {
      set({ isLoading: false });
    }
  },

  addRootMember: async (name) => {
    set({ isLoading: true, error: null });
    try {
      const response = await api.post<any>('api/members/root', { name });
      const newMember = transformMember(response);
      
      set(state => ({ 
        members: [...state.members, newMember],
        error: null
      }));

      return newMember;
    } catch (error) {
      console.error('Failed to add root member:', error);
      set({ 
        error: error instanceof AppError ? 
          error.message : 
          'حدث خطأ أثناء إضافة الفرد الأول'
      });
      throw error;
    } finally {
      set({ isLoading: false });
    }
  },

  updateMember: async (id, updates) => {
    set({ isLoading: true, error: null });
    try {
      const response = await api.post<any>(`api/members/${id}`, updates);
      const updatedMember = transformMember(response);

      set(state => ({
        members: state.members.map(m => 
          m.id === id ? { ...updatedMember, children: m.children } : m
        ),
        error: null
      }));
    } catch (error) {
      console.error('Failed to update member:', error);
      set({ 
        error: error instanceof AppError ? 
          error.message : 
          'حدث خطأ أثناء تحديث بيانات الفرد'
      });
      throw error;
    } finally {
      set({ isLoading: false });
    }
  },

  deleteMember: async (id) => {
    set({ isLoading: true, error: null });
    try {
      await api.delete(`api/members/${id}`);

      // Remove member and all descendants from local state
      set(state => {
        const getDescendants = (memberId: string): string[] => {
          const member = state.members.find(m => m.id === memberId);
          if (!member || !member.children.length) return [memberId];
          return [memberId, ...member.children.flatMap(childId => getDescendants(childId))];
        };

        const idsToRemove = getDescendants(id);
        return {
          members: state.members.filter(m => !idsToRemove.includes(m.id)),
          error: null
        };
      });
    } catch (error) {
      console.error('Failed to delete member:', error);
      set({ 
        error: error instanceof AppError ? 
          error.message : 
          'حدث خطأ أثناء حذف الفرد'
      });
      throw error;
    } finally {
      set({ isLoading: false });
    }
  },

  addPersonalInfo: (info) => {
    set(state => ({
      personalInfos: [...state.personalInfos, info]
    }));
  },

  updatePersonalInfo: (id, updates) => {
    set(state => ({
      personalInfos: state.personalInfos.map(info =>
        info.id === id ? { ...info, ...updates } : info
      )
    }));
  },

  deletePersonalInfo: (id) => {
    set(state => ({
      personalInfos: state.personalInfos.filter(info => info.id !== id)
    }));
  },

  addRequest: (request) => {
    set(state => ({
      requests: [...state.requests, request]
    }));
  },

  updateRequestStatus: (id, status) => {
    set(state => ({
      requests: state.requests.map(request =>
        request.id === id ? { ...request, status } : request
      )
    }));
  },

  updatePersonalInfoRequestStatus: (id, status) => {
    set(state => ({
      personalInfoRequests: state.personalInfoRequests.map(request =>
        request.id === id ? { ...request, status } : request
      )
    }));
  }
}));